/**
 * Right-side step inspector — Section 4 wireframe:
 *   STEP 2 of 3 — "60-day check-in"
 *   Pre-entry guard:  When loan.product != VA
 *   ACTIONS  ☑ Email borrower   ☑ WhatsApp borrower   ☐ Internal task
 *   COOLDOWN  [24 h]
 *   NEXT STEP  → 90-day survey   + Add branch
 *
 * Actions and guard edit a local StepSpec; the parent dispatches the patch
 * up into the WorkflowBody draft.
 */

import { useMemo } from 'react';
import type {
  ActionSpec,
  ConditionSpec,
  StepCategory,
  StepSpec,
  TransitionSpec,
} from '@mana/shared';
import { Badge } from '../../ui/Card.js';
import { Button } from '../../ui/Button.js';
import { Field, Input, Select } from '../../ui/Input.js';
import { GuardEditor } from './GuardEditor.js';

const CATEGORIES: StepCategory[] = ['NOTIFY', 'WAIT', 'BRANCH', 'TASK', 'TERMINAL'];

export interface StepInspectorProps {
  step: StepSpec;
  stepIndex: number;
  totalSteps: number;
  /** Steps after this one (legal next-step targets). */
  availableNextStepKeys: string[];
  /** Transitions starting at this step's key. */
  outgoingTransitions: TransitionSpec[];
  readOnly: boolean;
  onPatch: (next: StepSpec) => void;
  onPatchTransitions: (nextOutgoing: TransitionSpec[]) => void;
}

export function StepInspector({
  step,
  stepIndex,
  totalSteps,
  availableNextStepKeys,
  outgoingTransitions,
  readOnly,
  onPatch,
  onPatchTransitions,
}: StepInspectorProps) {
  const actionsByKind = useMemo(() => {
    return {
      hasEmail: step.actions.some((a) => a.kind === 'email'),
      hasWhatsapp: step.actions.some((a) => a.kind === 'whatsapp'),
      hasSms: step.actions.some((a) => a.kind === 'sms'),
      hasTask: step.actions.some((a) => a.kind === 'internal_task'),
    };
  }, [step.actions]);

  const toggleAction = (kind: ActionSpec['kind']) => {
    if (readOnly) return;
    const existing = step.actions.find((a) => a.kind === kind);
    if (existing) {
      onPatch({ ...step, actions: step.actions.filter((a) => a.kind !== kind) });
    } else {
      onPatch({ ...step, actions: [...step.actions, defaultActionOfKind(kind)] });
    }
  };

  // -----------------------------------------------------------------------
  return (
    <div className="space-y-5">
      <div className="border-b border-slate-100 pb-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          Step {stepIndex + 1} of {totalSteps}
        </p>
        <h3 className="mt-1 font-display text-lg font-semibold text-brand-navy-800">
          {step.name || '(unnamed step)'}
        </h3>
        <div className="mt-1 text-xs text-slate-500">
          Key <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">{step.key}</code>
        </div>
      </div>

      <Field label="Name">
        <Input
          value={step.name}
          onChange={(e) => onPatch({ ...step, name: e.target.value })}
          disabled={readOnly}
        />
      </Field>

      <Field label="Category">
        <Select
          value={step.category}
          onChange={(e) => onPatch({ ...step, category: e.target.value as StepCategory })}
          disabled={readOnly}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Field>

      <div>
        <span className="field-label">Pre-entry guard</span>
        <GuardEditor
          value={step.guardJson ?? null}
          readOnly={readOnly}
          onChange={(g) => onPatch({ ...step, guardJson: g ?? undefined })}
        />
      </div>

      <div>
        <span className="field-label">Actions</span>
        <div className="space-y-2 rounded-lg border border-slate-200 p-3 text-sm">
          <ActionToggle
            label="Email"
            checked={actionsByKind.hasEmail}
            onChange={() => toggleAction('email')}
            disabled={readOnly}
            detail={step.actions.find((a) => a.kind === 'email')}
            onDetailChange={(next) =>
              onPatch({
                ...step,
                actions: step.actions.map((a) => (a.kind === 'email' ? next : a)),
              })
            }
            readOnly={readOnly}
          />
          <ActionToggle
            label="WhatsApp"
            checked={actionsByKind.hasWhatsapp}
            onChange={() => toggleAction('whatsapp')}
            disabled={readOnly}
            detail={step.actions.find((a) => a.kind === 'whatsapp')}
            onDetailChange={(next) =>
              onPatch({
                ...step,
                actions: step.actions.map((a) => (a.kind === 'whatsapp' ? next : a)),
              })
            }
            readOnly={readOnly}
          />
          <ActionToggle
            label="SMS"
            checked={actionsByKind.hasSms}
            onChange={() => toggleAction('sms')}
            disabled={readOnly}
            detail={step.actions.find((a) => a.kind === 'sms')}
            onDetailChange={(next) =>
              onPatch({
                ...step,
                actions: step.actions.map((a) => (a.kind === 'sms' ? next : a)),
              })
            }
            readOnly={readOnly}
          />
          <ActionToggle
            label="Internal task"
            checked={actionsByKind.hasTask}
            onChange={() => toggleAction('internal_task')}
            disabled={readOnly}
            detail={step.actions.find((a) => a.kind === 'internal_task')}
            onDetailChange={(next) =>
              onPatch({
                ...step,
                actions: step.actions.map((a) => (a.kind === 'internal_task' ? next : a)),
              })
            }
            readOnly={readOnly}
          />
        </div>
      </div>

      <Field label="Cooldown (hours)" hint="Skip re-firing actions inside this window.">
        <Input
          type="number"
          min={0}
          max={720}
          value={step.cooldownHours}
          onChange={(e) =>
            onPatch({ ...step, cooldownHours: Math.max(0, parseInt(e.target.value, 10) || 0) })
          }
          disabled={readOnly}
        />
      </Field>

      <div>
        <span className="field-label">Next step</span>
        <BranchEditor
          outgoing={outgoingTransitions}
          availableTargets={availableNextStepKeys}
          readOnly={readOnly}
          onChange={onPatchTransitions}
          fromStepKey={step.key}
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={step.isTerminal}
          onChange={(e) => onPatch({ ...step, isTerminal: e.target.checked })}
          disabled={readOnly}
          className="h-4 w-4 rounded border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
        />
        Terminal — workflow completes after this step
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
function defaultActionOfKind(kind: ActionSpec['kind']): ActionSpec {
  switch (kind) {
    case 'email':
      return { kind: 'email', templateKey: 'default', to: 'borrower' };
    case 'whatsapp':
      return { kind: 'whatsapp', templateKey: 'default', to: 'borrower' };
    case 'sms':
      return { kind: 'sms', templateKey: 'default', to: 'borrower' };
    case 'internal_task':
      return { kind: 'internal_task', title: 'Follow up', assignee: 'mlo' };
  }
}

function ActionToggle({
  label,
  checked,
  onChange,
  detail,
  onDetailChange,
  readOnly,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  detail?: ActionSpec;
  onDetailChange: (next: ActionSpec) => void;
  readOnly: boolean;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="h-4 w-4 rounded border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
        />
        <span className="font-medium text-slate-800">{label}</span>
      </label>
      {checked && detail && (
        <div className="ml-6 mt-2 grid grid-cols-2 gap-2">
          {(detail.kind === 'email' ||
            detail.kind === 'whatsapp' ||
            detail.kind === 'sms') && (
            <>
              <label className="text-xs">
                <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-slate-500">
                  Template
                </span>
                <Input
                  value={detail.templateKey}
                  onChange={(e) =>
                    onDetailChange({ ...detail, templateKey: e.target.value })
                  }
                  disabled={readOnly}
                />
              </label>
              <label className="text-xs">
                <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-slate-500">
                  To
                </span>
                <Select
                  value={detail.to}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (detail.kind === 'email') {
                      onDetailChange({
                        ...detail,
                        to: v as 'borrower' | 'mlo' | 'processor' | 'custom',
                      });
                    } else {
                      onDetailChange({
                        ...detail,
                        to: v as 'borrower' | 'mlo' | 'custom',
                      });
                    }
                  }}
                  disabled={readOnly}
                >
                  <option value="borrower">Borrower</option>
                  <option value="mlo">MLO</option>
                  {detail.kind === 'email' && <option value="processor">Processor</option>}
                  <option value="custom">Custom</option>
                </Select>
              </label>
            </>
          )}
          {detail.kind === 'internal_task' && (
            <>
              <label className="col-span-2 text-xs">
                <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-slate-500">
                  Title
                </span>
                <Input
                  value={detail.title}
                  onChange={(e) => onDetailChange({ ...detail, title: e.target.value })}
                  disabled={readOnly}
                />
              </label>
              <label className="text-xs">
                <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-slate-500">
                  Assignee
                </span>
                <Select
                  value={detail.assignee}
                  onChange={(e) =>
                    onDetailChange({
                      ...detail,
                      assignee: e.target.value as 'owner' | 'mlo' | 'processor',
                    })
                  }
                  disabled={readOnly}
                >
                  <option value="owner">Loan owner</option>
                  <option value="mlo">MLO</option>
                  <option value="processor">Processor</option>
                </Select>
              </label>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function BranchEditor({
  outgoing,
  availableTargets,
  readOnly,
  onChange,
  fromStepKey,
}: {
  outgoing: TransitionSpec[];
  availableTargets: string[];
  readOnly: boolean;
  onChange: (next: TransitionSpec[]) => void;
  fromStepKey: string;
}) {
  const addBranch = () => {
    if (availableTargets.length === 0) return;
    onChange([
      ...outgoing,
      { fromStepKey, toStepKey: availableTargets[0]!, label: 'Branch' },
    ]);
  };

  if (outgoing.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-500">
        Linear flow — next step defaults to the following step in the list.
        <div className="mt-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={readOnly || availableTargets.length === 0}
            onClick={addBranch}
          >
            + Add branch
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3">
      {outgoing.map((t, idx) => (
        <div key={idx} className="flex items-center gap-2 text-sm">
          <span className="text-brand-navy-700">→</span>
          <select
            value={t.toStepKey}
            onChange={(e) => {
              const copy = [...outgoing];
              copy[idx] = { ...t, toStepKey: e.target.value };
              onChange(copy);
            }}
            disabled={readOnly}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
          >
            {availableTargets.map((tk) => (
              <option key={tk} value={tk}>
                {tk}
              </option>
            ))}
          </select>
          <Input
            value={t.label ?? ''}
            placeholder="label (optional)"
            onChange={(e) => {
              const copy = [...outgoing];
              copy[idx] = { ...t, label: e.target.value || undefined };
              onChange(copy);
            }}
            disabled={readOnly}
            className="flex-1"
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={readOnly}
            onClick={() => onChange(outgoing.filter((_, i) => i !== idx))}
            title="Remove branch"
          >
            ✕
          </Button>
        </div>
      ))}
      <Button variant="secondary" size="sm" disabled={readOnly} onClick={addBranch}>
        + Add branch
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action chip — used by the step list rows (not the inspector).
// ---------------------------------------------------------------------------
export function ActionChips({ actions }: { actions: ActionSpec[] }) {
  if (actions.length === 0) return <Badge tone="neutral">no actions</Badge>;
  return (
    <div className="flex flex-wrap gap-1">
      {actions.map((a, i) => {
        const tone: 'info' | 'gold' | 'warning' | 'neutral' =
          a.kind === 'email'
            ? 'info'
            : a.kind === 'whatsapp'
              ? 'gold'
              : a.kind === 'sms'
                ? 'warning'
                : 'neutral';
        return (
          <Badge key={i} tone={tone}>
            {a.kind}
          </Badge>
        );
      })}
    </div>
  );
}

// Re-export the ConditionSpec type so consumers can avoid a deep import.
export type { ConditionSpec };
